declare global {
  interface Window {
    turnstile?: {
      render(
        container: string | HTMLElement,
        options: {
          sitekey: string
          size?: 'normal' | 'compact' | 'flexible'
          callback?: (token: string) => void
          'error-callback'?: (error: unknown) => void
          'expired-callback'?: () => void
          'timeout-callback'?: () => void
          theme?: 'auto' | 'light' | 'dark'
          execution?: 'render' | 'execute'
          appearance?: 'always' | 'execute' | 'interaction-only'
        }
      ): string
      reset(widgetId?: string): void
      execute(container?: string | HTMLElement, options?: Record<string, unknown>): void
      remove(widgetId?: string): void
      getResponse(widgetId?: string): string
      isExpired?(widgetId?: string): boolean
    }
  }
}

// Cloudflare official test dummy site key (passes validation without user challenge)
const DEFAULT_SITEKEY = '1x00000000000000000000AA'
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const TOKEN_TTL_MS = 240_000 // 4 minutes (Cloudflare tokens are valid for 5 min)

let scriptLoadingPromise: Promise<void> | null = null
let widgetId: string | null = null
let containerEl: HTMLElement | null = null
let cachedToken: string | null = null
let tokenTimestamp = 0

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise((resolve) => {
    // If script already in DOM
    if (document.querySelector(`script[src*="challenges.cloudflare.com/turnstile"]`)) {
      const check = setInterval(() => {
        if (window.turnstile) {
          clearInterval(check)
          resolve()
        }
      }, 50)
      return
    }

    const script = document.createElement('script')
    script.src = SCRIPT_URL
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      console.warn('[Turnstile] Script failed to load (adblocker or offline). Proceeding gracefully.')
      resolve()
    }
    document.head.appendChild(script)
  })

  return scriptLoadingPromise
}

function getOrCreateContainer(): HTMLElement {
  if (!containerEl || !document.body.contains(containerEl)) {
    containerEl = document.createElement('div')
    containerEl.id = 'turnstile-container'
    containerEl.setAttribute('aria-hidden', 'true')
    // Dock container non-intrusively in corner instead of coordinate displacement (-9999px)
    // which anti-bot heuristics flag as deceptive concealment.
    // With appearance: 'interaction-only', Turnstile renders invisible (0x0) unless user interaction is needed.
    containerEl.style.position = 'fixed'
    containerEl.style.bottom = '16px'
    containerEl.style.right = '16px'
    containerEl.style.zIndex = '100001'
    containerEl.style.maxWidth = '100vw'
    document.body.appendChild(containerEl)
  }
  return containerEl
}

/**
 * Pre-warms Cloudflare Turnstile in the background so a valid challenge token
 * is ready when the visitor sends a message.
 */
export function warmupTurnstile(): void {
  if (typeof window === 'undefined') return
  getTurnstileToken().catch(() => {})
}

/**
 * Obtains a Cloudflare Turnstile token.
 * Resolves within 4 seconds or falls back to an empty string to guarantee
 * that adblockers or privacy add-ons never block legitimate user messages.
 */
export async function getTurnstileToken(): Promise<string> {
  const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || DEFAULT_SITEKEY

  // Return cached token if fresh
  if (cachedToken && Date.now() - tokenTimestamp < TOKEN_TTL_MS) {
    if (window.turnstile && widgetId !== null) {
      const liveToken = window.turnstile.getResponse(widgetId)
      if (liveToken) return liveToken
    }
    return cachedToken
  }

  try {
    await loadTurnstileScript()
  } catch {
    return ''
  }

  const turnstile = window.turnstile
  if (!turnstile) {
    return ''
  }

  // If already rendered and ready, read current response
  if (widgetId !== null) {
    const liveToken = turnstile.getResponse(widgetId)
    if (liveToken) {
      cachedToken = liveToken
      tokenTimestamp = Date.now()
      return liveToken
    }
  }

  return new Promise<string>((resolve) => {
    let resolved = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const safeResolve = (token: string) => {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      if (!resolved) {
        resolved = true
        if (token) {
          cachedToken = token
          tokenTimestamp = Date.now()
        }
        resolve(token)
      }
    }

    // Safety timeout: never hang user submit button if network is slow or adblocked
    timer = setTimeout(() => {
      safeResolve('')
    }, 4000)

    try {
      const container = getOrCreateContainer()

      // Cleanly remove any existing widget BEFORE touching container to avoid:
      // "[Cloudflare Turnstile] Cannot find Widget cf-chl-widget-..., consider using turnstile.remove()"
      if (widgetId !== null) {
        try {
          turnstile.remove(widgetId)
        } catch {
          // ignore cleanup errors if widget was already dismantled
        }
        widgetId = null
      }

      container.innerHTML = ''

      // Valid size values in Turnstile: 'normal' | 'compact' | 'flexible'
      // appearance: 'interaction-only' keeps it invisible until interactive verification is required.
      widgetId = turnstile.render(container, {
        sitekey: siteKey,
        size: 'flexible',
        appearance: 'interaction-only',
        callback: (token: string) => {
          safeResolve(token)
        },
        'error-callback': (err: unknown) => {
          console.warn('[Turnstile] Challenge error:', err)
          safeResolve('')
        },
        'timeout-callback': () => {
          safeResolve('')
        },
        'expired-callback': () => {
          cachedToken = null
          if (widgetId) {
            try {
              turnstile.remove(widgetId)
            } catch {}
            widgetId = null
          }
        },
      })
    } catch (err) {
      console.warn('[Turnstile] Render execution error:', err)
      safeResolve('')
    }
  })
}

/**
 * Resets cached token after consumption so subsequent requests get a fresh token.
 */
export function consumeTurnstileToken(): void {
  cachedToken = null
  tokenTimestamp = 0
  if (widgetId !== null && window.turnstile) {
    try {
      window.turnstile.reset(widgetId)
    } catch {}
  }
}

export function cleanupTurnstile(): void {
  cachedToken = null
  tokenTimestamp = 0
  if (widgetId !== null && window.turnstile) {
    try {
      window.turnstile.remove(widgetId)
    } catch {}
    widgetId = null
  }
}

